import { Settings, LogOut, ChevronLeft, ChevronRight, EyeIcon, EyeOffIcon, RefreshCcw, Clock } from 'lucide-react'
import { formatDate } from '../utils/dateUtils';
import { useState, useRef, useEffect } from 'react';
import { authService } from '../services/auth.service';
import Dialog from '../components/Dialog';
import { toast } from 'react-toastify';
import { blitzService } from '../services/blitz.service';
import { NetworkStatusIndicator } from '../components/NetworkStatusIndicator';
import { useLocation } from 'react-router-dom';

interface MenuItem {
  label: string;
  icon: React.JSX.Element;
  onClick?: () => void;
}

interface TopbarProps {
  toggle: () => void;
  isOpen: boolean;
}

interface BlitzStatus {
  isActive: boolean;
  schedule?: {
    isScheduled: boolean;
    weekdays: string[];
    startTime: string;
    endTime: string;
    startDate: string;
    endDate: string;
  };
}

export default function Topbar({ toggle, isOpen }: TopbarProps) {
  const [user, setUser] = useState<any>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isBlitzModeOpen, setIsBlitzModeOpen] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [blitzModeForm, setBlitzModeForm] = useState({
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: ''
  });
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false
  });
  const [errors, setErrors] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [blitzStatus, setBlitzStatus] = useState<BlitzStatus | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const blitzModeRef = useRef<HTMLDivElement>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isActivateConfirmOpen, setIsActivateConfirmOpen] = useState(false);
  const [isDeactivateConfirmOpen, setIsDeactivateConfirmOpen] = useState(false);
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isScheduleWarningOpen, setIsScheduleWarningOpen] = useState(false);
  const [isDeactivateWarningOpen, setIsDeactivateWarningOpen] = useState(false);
  const [successScheduleInfo, setSuccessScheduleInfo] = useState<{ startDate: string; startTime: string; endDate: string; endTime: string; } | null>(null);
  const [blitzFormErrors, setBlitzFormErrors] = useState({
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: ''
  });
  const location = useLocation();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isBlitzNotificationOpen, setIsBlitzNotificationOpen] = useState(false);
  const blitzNotificationRef = useRef<HTMLDivElement>(null);

  // Get user data from auth service
  const getUser = async () => {
    const user = await authService.getUser();
    setUser(user);
  }

  useEffect(() => {
    getUser();
    // Get initial blitz status
    getBlitzStatus();
  }, []);

  // Update getBlitzStatus function
  const getBlitzStatus = async () => {
    try {
      const response = await blitzService.getBlitzStatus();

      // Check if response exists and has the expected structure
      if (response && typeof response === 'object' && 'isActive' in response) {
        const typedResponse = response as unknown as BlitzStatus;
        setBlitzStatus(typedResponse);
      } else {
        console.error('Invalid response structure:', response);
      }
    } catch (error) {
      console.error('Error fetching blitz status:', error);
    }
  };

  const menuItems: MenuItem[] = [
    {
      label: 'Change Password',
      icon: <Settings size={16} />,
      onClick: () => {
        setIsChangePasswordOpen(true);
        setDropdownOpen(false);
      }
    },
    {
      label: 'Logout',
      icon: <LogOut size={16} />,
      onClick: () => {
        authService.logout();
        setDropdownOpen(false);
      }
    }
  ];


  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
      if (blitzModeRef.current && !blitzModeRef.current.contains(event.target as Node)) {
        setIsBlitzModeOpen(false);
      }
      if (blitzNotificationRef.current && !blitzNotificationRef.current.contains(event.target as Node)) {
        setIsBlitzNotificationOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Add validation function for blitz mode form
  const validateBlitzModeForm = () => {
    let isValid = true;
    const newErrors = {
      startDate: '',
      startTime: '',
      endDate: '',
      endTime: ''
    };

    // Validate start date
    if (!blitzModeForm.startDate) {
      newErrors.startDate = 'Start date is required';
      isValid = false;
    }

    // Validate start time
    if (!blitzModeForm.startTime) {
      newErrors.startTime = 'Start time is required';
      isValid = false;
    }

    // Validate end date
    if (!blitzModeForm.endDate) {
      newErrors.endDate = 'End date is required';
      isValid = false;
    }

    // Validate end time
    if (!blitzModeForm.endTime) {
      newErrors.endTime = 'End time is required';
      isValid = false;
    }

    // Validate date range
    if (blitzModeForm.startDate && blitzModeForm.endDate) {
      const startDateTime = new Date(`${blitzModeForm.startDate}T${blitzModeForm.startTime || '00:00'}`);
      const endDateTime = new Date(`${blitzModeForm.endDate}T${blitzModeForm.endTime || '00:00'}`);

      if (endDateTime <= startDateTime) {
        newErrors.endDate = 'End date must be after start date';
        isValid = false;
      }
    }

    setBlitzFormErrors(newErrors);
    return isValid;
  };

  const handleBlitzModeSubmit = () => {
    if (!validateBlitzModeForm()) {
      return;
    }

    setIsDialogOpen(false);
    setIsConfirmationOpen(true);
  };

  // Update closeDialog to clear errors
  const closeDialog = () => {
    setIsDialogOpen(false);
    setBlitzFormErrors({
      startDate: '',
      startTime: '',
      endDate: '',
      endTime: ''
    });
    // Reset form values as well
    setBlitzModeForm({
      startDate: '',
      startTime: '',
      endDate: '',
      endTime: ''
    });
  };

  const handleConfirmBlitzMode = async () => {
    try {
      // Format the dates to ISO string format
      // const startDateTime = new Date(`${blitzModeForm.startDate}T${blitzModeForm.startTime}`).toISOString();
      // const endDateTime = new Date(`${blitzModeForm.endDate}T${blitzModeForm.endTime}`).toISOString();

      // Create the formatted schedule
      const formattedSchedule = {
        isScheduled: true,
        weekdays: [
          "monday",
          "tuesday",
          "wednesday",
          "Thursday",
          "friday",
        ],
        startTime: blitzModeForm.startTime,
        endTime: blitzModeForm.endTime,
        startDate: blitzModeForm.startDate,
        endDate: blitzModeForm.endDate
      };

      // Call the blitz service to schedule
      await blitzService.scheduleBlitzMode(formattedSchedule);

      // Get updated blitz status after successful scheduling
      await getBlitzStatus();

      setIsConfirmationOpen(false);
      setSuccessScheduleInfo({
        startDate: blitzModeForm.startDate,
        startTime: blitzModeForm.startTime,
        endDate: blitzModeForm.endDate,
        endTime: blitzModeForm.endTime,
      });
      setIsSuccessDialogOpen(true);

      // Reset form after successful scheduling
      setBlitzModeForm({
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: ''
      });
    } catch (error: any) {
      console.error('Error scheduling blitz mode:', error);
      setIsConfirmationOpen(false);
    }
  };

  // Add revert handler
  const handleRevertBlitzMode = () => {
    setIsConfirmationOpen(false);
    setBlitzModeForm({
      startDate: '',
      startTime: '',
      endDate: '',
      endTime: ''
    });
  };

  // Password validation function
  const validatePasswordForm = () => {
    let isValid = true;
    const newErrors = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    };

    // Current password validation
    if (!passwordForm.currentPassword.trim()) {
      newErrors.currentPassword = 'Current password is required';
      isValid = false;
    }

    // New password validation
    if (!passwordForm.newPassword.trim()) {
      newErrors.newPassword = 'New password is required';
      isValid = false;
    } else {
      // Password requirements regex checks
      const hasUpperCase = /[A-Z]/.test(passwordForm.newPassword);
      const hasLowerCase = /[a-z]/.test(passwordForm.newPassword);
      const hasNumber = /\d/.test(passwordForm.newPassword);
      const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(passwordForm.newPassword);

      if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSpecialChar) {
        newErrors.newPassword = 'Password needs upper, lower, number & special character.';
        isValid = false;
      }
    }

    // Confirm password validation
    if (!passwordForm.confirmPassword.trim()) {
      newErrors.confirmPassword = 'Please confirm your new password';
      isValid = false;
    } else if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Password change handler
  const handlePasswordChange = async () => {
    if (validatePasswordForm()) {
      try {
        const currentUser = await authService.getUser();

        if (!currentUser?.id) {
          throw new Error('User not found');
        }

        await authService.changePassword({
          id: currentUser.id,
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword,
          confirmPassword: passwordForm.confirmPassword
        });

        setIsChangePasswordOpen(false);
        // Reset form and states
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setShowPasswords({
          currentPassword: false,
          newPassword: false,
          confirmPassword: false
        });
        setErrors({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });

        // Add success toast
        toast.success('Password changed successfully');
      } catch (error: any) {
        console.error('Password change error:', error);
        const errorMessage = error?.message || 'Failed to change password';
        setErrors(prev => ({
          ...prev,
          currentPassword: errorMessage
        }));
        // Add error toast
        toast.error(errorMessage);
      }
    }
  };

  // Password form input handler
  const handlePasswordFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const toggleBlitzMode = (e: React.MouseEvent) => {
    e.stopPropagation(); // Stop event propagation
    setIsBlitzModeOpen(prev => !prev);
  };

  const openDialog = () => {
    // Pre-populate form with existing schedule values when editing
    if (blitzStatus?.schedule?.isScheduled) {
      const { startDate, endDate, startTime, endTime } = blitzStatus.schedule;
      setBlitzModeForm({
        startDate: startDate ? startDate.slice(0, 10) : '',
        startTime: startTime ? startTime.slice(0, 5) : '',
        endDate: endDate ? endDate.slice(0, 10) : '',
        endTime: endTime ? endTime.slice(0, 5) : '',
      });
    }
    setIsDialogOpen(true);
    setIsBlitzModeOpen(false);
  };

  const handleBlitzModeClick = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Stop event propagation
    // First check if we have a valid status
    if (!blitzStatus) {
      await getBlitzStatus();
      return;
    }

    if (blitzStatus.isActive === true) {
      if (blitzStatus.schedule?.isScheduled) {
        setIsDeactivateWarningOpen(true);
      } else {
        setIsDeactivateConfirmOpen(true);
      }
    } else {
      // Check if there's already a scheduled time before activating
      if (blitzStatus.schedule?.isScheduled) {
        setIsScheduleWarningOpen(true);
      } else {
        setIsActivateConfirmOpen(true);
      }
    }
    setIsBlitzModeOpen(false); // Close the panel when activating/deactivating
  };

  const handleActivateBlitzMode = async () => {
    try {
      await blitzService.activateBlitzMode();
      await getBlitzStatus(); // Refresh status
      setIsActivateConfirmOpen(false);
      setSuccessScheduleInfo(null);
      setSuccessMessage('Blitz mode activated successfully!');
      setIsSuccessDialogOpen(true);
    } catch (error: any) {
      console.error('Error activating blitz mode:', error);
      setIsActivateConfirmOpen(false);
    }
  };

  const handleDeactivateBlitzMode = async () => {
    try {
      // If a schedule exists, cancel it entirely (clears isActive + isScheduled).
      // Plain deactivate only sets isActive=false and the schedule would restart.
      if (blitzStatus?.schedule?.isScheduled) {
        await blitzService.cancelSchedule();
      } else {
        await blitzService.deactivateBlitzMode();
      }
      await getBlitzStatus(); // Refresh status
      setIsDeactivateConfirmOpen(false);
      setSuccessScheduleInfo(null);
      setSuccessMessage('Blitz mode deactivated successfully!');
      setIsSuccessDialogOpen(true);
    } catch (error: any) {
      console.error('Error deactivating blitz mode:', error);
      setIsDeactivateConfirmOpen(false);
    }
  };

  const handleCancelSchedule = async () => {
    try {
      await blitzService.cancelSchedule();
      await getBlitzStatus();
      setIsBlitzModeOpen(false);
      setSuccessScheduleInfo(null);
      setSuccessMessage('Blitz mode schedule canceled.');
      setIsSuccessDialogOpen(true);
    } catch (error: any) {
      console.error('Error canceling blitz mode schedule:', error);
      toast.error('Failed to cancel blitz mode schedule. Please try again.');
    }
  };

  // Handler for refresh icon click
  const handleRefreshBlitzStatus = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsRefreshing(true);
    getBlitzStatus();
    // If on /jobsite-management, fire the custom event
    if (location.pathname === '/jobsite-management') {
      window.dispatchEvent(new CustomEvent('REFRESH_JOBSITES'));
    }
    // End animation after 1s
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  return (
    <header className="bg-white h-16 flex items-center justify-between border-b border-gray-200 px-4 fixed w-full z-20">

      <div className="flex lg:hidden items-center space-x-2">
        <button 
          className="flex items-center px-1 rounded-full transition duration-200" 
          onClick={toggle}
          data-testid="toggle-menu-button"
        >
          <img src="/list.svg" alt="Menu" width={30} height={30} className="text-gray-300" />
        </button>
      </div>

      <div className="flex items-center space-x-4">
        <div className="flex items-center w-48">
          <img src="/logo.png?v=2" alt="UA Canada Logo" />
          <button
            onClick={toggle}
            className="p-1.5 hover:bg-gray-100 rounded-full transition-colors duration-200 mt-2 hidden lg:flex"
            aria-label={isOpen ? "Close sidebar" : "Open sidebar"}
            data-testid="toggle-menu-button"
          >
            {isOpen ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
          </button>
        </div>
      </div>

      <div className="flex">

        {/* Blitz Mode Notification Chip for md and below */}
        {(user?.role === 'organizer' || user?.role === 'admin') && blitzStatus?.schedule?.isScheduled && (
          <div className="flex md:hidden items-center w-full" ref={blitzNotificationRef}>
            <button
              className="flex items-center gap-2 px-3 py-1.5 bg-yellow-100 text-yellow-800 rounded-full shadow border border-yellow-300 text-xs font-medium hover:bg-yellow-200 transition relative"
              onClick={() => setIsBlitzNotificationOpen((prev) => !prev)}
              aria-label="Blitz mode schedule details"
              type="button"
            >
              <Clock className="w-4 h-4 text-yellow-600" />
            </button>
            {/* Panel/Popover */}
            {isBlitzNotificationOpen && (
              <div className="fixed top-16 left-0 w-full max-w-full bg-white border-t border-gray-200 rounded-t-lg shadow-lg z-50 p-4 animate-fade-in">
                <div className="flex items-center mb-2">
                  <Clock className="w-5 h-5 text-yellow-600 mr-2" />
                  <span className="font-semibold text-sm text-gray-800">Blitz Mode Schedule</span>
                </div>
                <div className="text-xs text-gray-700">
                  <div className="mb-1">
                    <span className="font-medium">From:</span> {`${formatDate(blitzStatus.schedule.startDate)}, ${blitzStatus.schedule.startTime} ET`}
                  </div>
                  <div>
                    <span className="font-medium">To:</span> {`${formatDate(blitzStatus.schedule.endDate)}, ${blitzStatus.schedule.endTime} ET`}
                  </div>
                </div>
                <button
                  className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
                  onClick={() => setIsBlitzNotificationOpen(false)}
                  aria-label="Close blitz schedule panel"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        )}

        {user?.role === 'organizer' && (
          <>
            {blitzStatus?.schedule?.isScheduled && (
              <div className="hidden md:flex items-center text-xs text-black text-right">
                {blitzStatus?.isActive ? 'Blitz mode is Active' : 'Blitz mode is Scheduled'}<br />
                From: {`${formatDate(blitzStatus.schedule.startDate)}, ${blitzStatus.schedule.startTime} ET `}
                To: {`${formatDate(blitzStatus.schedule.endDate)}, ${blitzStatus.schedule.endTime} ET`}
                {/* Refresh icon for organizer */}
                <button
                  className="ml-2 px-2 py-2 rounded-full hover:bg-gray-100 transition-colors duration-200"
                  onClick={handleRefreshBlitzStatus}
                  id="refresh-blitz-status-organizer"
                  aria-label="Refresh Blitz Status"
                  type="button"
                >
                  <RefreshCcw className={`h-4 w-4 transition-transform duration-500 ${isRefreshing ? 'animate-spin' : ''}`} />
                </button>
              </div>
            )}
            {blitzStatus?.isActive && (
              <div className="flex bg-green-100 text-green-800 text-[16px] font-medium px-3 py-2 rounded-full">
                <span className="mr-0 md:mr-1">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
                  </svg>
                </span>
                <span> Activated Blitz mode</span>
              </div>
            )}
            {<NetworkStatusIndicator />}
          </>
        )}

        {user?.role === 'admin' && (
          <>
            {blitzStatus?.schedule?.isScheduled && (
              <div className="hidden md:flex items-center text-xs text-black text-right">
                {blitzStatus?.isActive ? 'Blitz mode is Active' : 'Blitz mode is Scheduled'}<br />
                From: {`${formatDate(blitzStatus.schedule.startDate)}, ${blitzStatus.schedule.startTime} ET `}
                To: {`${formatDate(blitzStatus.schedule.endDate)}, ${blitzStatus.schedule.endTime} ET`}
              </div>
            )}
            {<NetworkStatusIndicator />}

            <div className="relative ml-2" ref={blitzModeRef}>
              <button
                className={`px-1 flex items-center ${blitzStatus?.isActive ? 'bg-[#E8E8E8] text-[#767A81]' : 'btn-primary text-white'} rounded-full transition duration-200`}
              >
                <div className='px-2 py-2 flex' onClick={handleBlitzModeClick} id="blitz-mode-button" data-testid={blitzStatus?.isActive ? "blitz-mode-active" : "blitz-mode-button"}>
                  <span className="mr-0 md:mr-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5  ${blitzStatus?.isActive ? 'text-[#767A81]' : 'text-[#E6D69B]'}`} fill={`${blitzStatus?.isActive ? '#767A81' : '#E6D69B '}`} viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
                    </svg>
                  </span>
                  <span className="hidden md:inline font-medium text-base">
                    {blitzStatus?.isActive ? 'Deactivate Blitz Mode' : 'Blitz Mode'}
                  </span>
                </div>
                {!blitzStatus?.isActive && (
                  <>
                    <div className="border-l border-gray-300 h-6"></div>
                    <div className='px-2 py-2' onClick={(e) => toggleBlitzMode(e)} id="toggle-blitz-dropdown" data-testid="toggle-blitz-dropdown">
                      <svg className={`h-4 w-4 transform transition-transform ${isBlitzModeOpen ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </>
                )}

              </button>

              {isBlitzModeOpen && (
                <div className="absolute left-[-8px] md:left-[-8px] max-md:left-[-94px] mt-2 w-44 bg-white rounded-md shadow-xl border border-silver z-10">
                  <div className="p-2">
                    <button
                      onClick={openDialog}
                      className="w-full text-left text-[14px] py-2 px-2 rounded-md hover:bg-gray-100"
                      id="schedule-blitz-button"
                      data-testid="schedule-blitz-button"
                    >
                      {blitzStatus?.schedule?.isScheduled ? 'Edit Blitz Schedule' : 'Schedule Blitz Mode'}
                    </button>
                    {blitzStatus?.schedule?.isScheduled && (
                      <button
                        onClick={() => { setIsBlitzModeOpen(false); setIsDeactivateWarningOpen(true); }}
                        className="w-full text-left text-[14px] py-2 px-2 rounded-md hover:bg-gray-100 text-red-600"
                        id="cancel-blitz-schedule-button"
                        data-testid="cancel-blitz-schedule-button"
                      >
                        Cancel Schedule
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* <div className="relative flex items-center space-x-2" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex items-center justify-center rounded-full h-8 w-8 bg-gray-200 hover:bg-gray-300 focus:outline-none border-0"
            aria-label="User menu"
            data-testid="user-avatar"
          >
            {user?.first_name?.[0]}{user?.last_name?.[0]}
          </button>

          <div
            className={`absolute right-0 mt-2 w-48 bg-white border border-gray-300 rounded-lg shadow-lg transition-all duration-200 ease-in-out ${dropdownOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'
              }`}
            style={{ top: '38px' }}
          >
            <ul className="py-1" role="menu">
              {menuItems.map((item, index) => (
                <li key={index} role="menuitem">
                  <button
                    onClick={item.onClick}
                    className="w-full px-4 py-2 text-[14px] text-gray-700 hover:bg-gray-100 flex items-center justify-between"
                    data-testid={item.label === 'Change Password' ? 'change-password-option' : 'logout-option'}
                  >
                    {item.label}
                    {item.icon}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div> */}
      </div>

      {/* Blitz Mode Dialog */}
      <Dialog
        isOpen={isDialogOpen}
        onClose={closeDialog}
        title={blitzStatus?.schedule?.isScheduled ? 'Edit Blitz Schedule' : 'Schedule Blitz Mode'}
        id="blitz-mode-dialog"
        width="w-[95%] sm:w-[500px]"
      >
        <div className="space-y-6">
          <p className="text-sm text-gray-600">Choose the Start and End Date (All times in ET)</p>

          <div className="space-y-4">
            {/* Start Date/Time */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start <span className="text-red-500">*</span></label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <input
                    type="date"
                    value={blitzModeForm.startDate}
                    min={new Date().toISOString().split('T')[0]}
                    onChange={(e) => setBlitzModeForm(prev => ({ ...prev, startDate: e.target.value }))}
                    className={`w-full px-3 py-2 border ${blitzFormErrors.startDate ? 'border-red-500' : 'border-gray-300'} rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black text-sm`}
                    id="blitz-start-date"
                    data-testid="blitz-start-date"
                  />
                  {blitzFormErrors.startDate && (
                    <p className="text-xs text-red-600 mt-1">{blitzFormErrors.startDate}</p>
                  )}
                </div>
                <div>
                  <input
                    type="time"
                    value={blitzModeForm.startTime}
                    onChange={(e) => setBlitzModeForm(prev => ({ ...prev, startTime: e.target.value }))}
                    className={`w-full px-3 py-2 border ${blitzFormErrors.startTime ? 'border-red-500' : 'border-gray-300'} rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black text-sm`}
                    id="blitz-start-time"
                    data-testid="blitz-start-time"
                  />
                  {blitzFormErrors.startTime && (
                    <p className="text-xs text-red-600 mt-1">{blitzFormErrors.startTime}</p>
                  )}
                </div>
              </div>
            </div>

            {/* End Date/Time */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End <span className="text-red-500">*</span></label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <input
                    type="date"
                    value={blitzModeForm.endDate}
                    min={blitzModeForm.startDate || new Date().toISOString().split('T')[0]}
                    onChange={(e) => setBlitzModeForm(prev => ({ ...prev, endDate: e.target.value }))}
                    className={`w-full px-3 py-2 border ${blitzFormErrors.endDate ? 'border-red-500' : 'border-gray-300'} rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black text-sm`}
                    id="blitz-end-date"
                    data-testid="blitz-end-date"
                  />
                  {blitzFormErrors.endDate && (
                    <p className="text-xs text-red-600 mt-1">{blitzFormErrors.endDate}</p>
                  )}
                </div>
                <div>
                  <input
                    type="time"
                    value={blitzModeForm.endTime}
                    onChange={(e) => setBlitzModeForm(prev => ({ ...prev, endTime: e.target.value }))}
                    className={`w-full px-3 py-2 border ${blitzFormErrors.endTime ? 'border-red-500' : 'border-gray-300'} rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black text-sm`}
                    id="blitz-end-time"
                    data-testid="blitz-end-time"
                  />
                  {blitzFormErrors.endTime && (
                    <p className="text-xs text-red-600 mt-1">{blitzFormErrors.endTime}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={closeDialog}
              className="btn-secondary"
              id="cancel-blitz-schedule"
            >
              Cancel
            </button>
            <button
              onClick={handleBlitzModeSubmit}
              className="btn-primary"
              id="submit-blitz-schedule"
              data-testid="submit-blitz-schedule"
            >
              Schedule Blitz Mode
            </button>
          </div>
        </div>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        isOpen={isConfirmationOpen}
        onClose={() => setIsConfirmationOpen(false)}
        width="w-[95%] sm:w-[500px]"
        padding="px-6 py-8 sm:px-10 sm:py-10"
        className="z-[60]"
        id="blitz-mode-confirmation-dialog"
      >
        <div className="text-center">
          <h3 className="text-heading font-heading mb-2">
            {blitzStatus?.schedule?.isScheduled ? 'Edit Blitz Schedule' : 'Schedule Blitz Mode'}
          </h3>
          <p className="text-text-secondary text-body mb-2">
            Are you sure you want to {blitzStatus?.schedule?.isScheduled ? 'update' : 'schedule'} blitz mode with the following times?
          </p>
          <div className="bg-gray-50 rounded-lg p-4 mb-6 text-sm">
            <div className="mb-1">
              <span className="font-medium">Start:</span> {`${formatDate(blitzModeForm.startDate)}, ${blitzModeForm.startTime}:00 ET`}
            </div>
            <div>
              <span className="font-medium">End:</span> {`${formatDate(blitzModeForm.endDate)}, ${blitzModeForm.endTime}:00 ET`}
            </div>
          </div>

          <div className="flex justify-center gap-4">
            <button
              onClick={handleRevertBlitzMode}
              className="btn-secondary px-8"
              id="revert-blitz-schedule"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmBlitzMode}
              className="btn-primary px-8"
              id="confirm-blitz-schedule"
              data-testid="confirm-schedule-button"
            >
              Confirm
            </button>
          </div>
        </div>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog
        isOpen={isChangePasswordOpen}
        onClose={() => {
          setIsChangePasswordOpen(false);
          setPasswordForm({
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          });
          // Reset password visibility
          setShowPasswords({
            currentPassword: false,
            newPassword: false,
            confirmPassword: false
          });
          // Reset errors
          setErrors({
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          });
        }}
        title="Change Password"
        id="change-password-dialog"
        width="w-[95%] sm:w-[600px] md:w-[700px] lg:w-[500px]"
        padding="px-3 py-4 sm:px-4 sm:py-5 md:px-10 md:py-8 lg:px-6 lg:py-6"
      >
        <div className="max-h-[80vh] sm:max-h-[70vh] overflow-y-auto scrollbar-hide">
          <div className="mt-4 sm:mt-6">
            <div className="space-y-4">
              <div className="relative pb-4">
                <label className="block text-[14px] font-medium text-[#767A81] mb-1">
                  Current Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    id="current-password-input"
                    type={showPasswords.currentPassword ? 'text' : 'password'}
                    name="currentPassword"
                    value={passwordForm.currentPassword}
                    onChange={handlePasswordFormChange}
                    className={`w-full px-3 py-2 text-[16px] border ${errors.currentPassword ? 'border-red-500' : 'border-border'} rounded-input focus:outline-none focus:ring-1 focus:ring-black focus:border-black focus:ring-opacity-0`}
                    placeholder="Enter Current Password"
                    data-testid="current-password-input"
                  />
                  <button
                    id="toggle-current-password-visibility"
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPasswords(prev => ({
                      ...prev,
                      currentPassword: !prev.currentPassword
                    }))}
                    data-testid="toggle-password-visibility"
                  >
                    {showPasswords.currentPassword ? (
                      <EyeOffIcon className="h-5 w-5 text-gray-400" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.currentPassword && (
                  <p id="current-password-error" className="absolute -bottom-1 right-0 text-small text-red-600">{errors.currentPassword}</p>
                )}
              </div>

              <div className="relative pb-4">
                <label className="block text-[14px] font-medium text-[#767A81] mb-1">
                  New Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.newPassword ? 'text' : 'password'}
                    name="newPassword"
                    value={passwordForm.newPassword}
                    onChange={handlePasswordFormChange}
                    className={`w-full px-3 py-2 text-[16px] border ${errors.newPassword ? 'border-red-500' : 'border-border'} rounded-input focus:outline-none focus:ring-1 focus:ring-black focus:border-black focus:ring-opacity-0`}
                    placeholder="Enter New Password"
                    id="new-password-input"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPasswords(prev => ({
                      ...prev,
                      newPassword: !prev.newPassword
                    }))}
                    data-testid="toggle-password-visibility"
                  >
                    {showPasswords.newPassword ? (
                      <EyeOffIcon className="h-5 w-5 text-gray-400" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.newPassword && (
                  <p className="absolute -bottom-1 right-0 text-small text-red-600" id="new-password-error">{errors.newPassword}</p>
                )}
              </div>

              <div className="relative pb-4">
                <label className="block text-[14px] font-medium text-[#767A81] mb-1">
                  Confirm New Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.confirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={passwordForm.confirmPassword}
                    onChange={handlePasswordFormChange}
                    className={`w-full px-3 py-2 text-[16px] border ${errors.confirmPassword ? 'border-red-500' : 'border-border'} rounded-input focus:outline-none focus:ring-1 focus:ring-black focus:border-black focus:ring-opacity-0`}
                    placeholder="Confirm New Password"
                    id="confirm-password-input"
                    data-testid="confirm-password-input"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPasswords(prev => ({
                      ...prev,
                      confirmPassword: !prev.confirmPassword
                    }))}
                    data-testid="toggle-password-visibility"
                  >
                    {showPasswords.confirmPassword ? (
                      <EyeOffIcon className="h-5 w-5 text-gray-400" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="absolute -bottom-1 right-0 text-small text-red-600" id="confirm-password-error">{errors.confirmPassword}</p>
                )}
              </div>
            </div>
          </div>

          <div className="sticky bottom-0 bg-white pt-8 mt-8">
            <button
              id="submit-password-change"
              onClick={handlePasswordChange}
              className="btn-primary w-full"
              data-testid="submit-password-change"
            >
              Change Password
            </button>
          </div>
        </div>
      </Dialog>

      {/* Schedule Warning Dialog */}
      <Dialog
        isOpen={isScheduleWarningOpen}
        onClose={() => setIsScheduleWarningOpen(false)}
        width="w-[95%] sm:w-[500px]"
        padding="px-6 py-8 sm:px-10 sm:py-10"
        className="z-[60]"
        id="schedule-warning-dialog"
      >
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-yellow-50 flex items-center justify-center">
              <Clock className="w-8 h-8 text-yellow-500" />
            </div>
          </div>
          <h3 className="text-heading font-heading mb-2">Blitz Mode Already Scheduled</h3>
          <p className="text-text-secondary text-body mb-2">
            Blitz mode is currently scheduled from:
          </p>
          {blitzStatus?.schedule && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-sm">
              <div className="mb-1">
                <span className="font-medium">Start:</span> {`${formatDate(blitzStatus.schedule.startDate)}, ${blitzStatus.schedule.startTime} ET`}
              </div>
              <div>
                <span className="font-medium">End:</span> {`${formatDate(blitzStatus.schedule.endDate)}, ${blitzStatus.schedule.endTime} ET`}
              </div>
            </div>
          )}
          <p className="text-text-secondary text-body mb-8">
            Activating now will override the scheduled time. Do you want to continue?
          </p>

          <div className="flex justify-center gap-4">
            <button
              onClick={() => setIsScheduleWarningOpen(false)}
              className="btn-secondary px-8"
              id="cancel-schedule-warning"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                setIsScheduleWarningOpen(false);
                setIsActivateConfirmOpen(true);
              }}
              className="btn-primary px-8"
              id="proceed-with-activation"
              data-testid="proceed-with-activation"
            >
              Continue
            </button>
          </div>
        </div>
      </Dialog>

      {/* Activate Confirmation Dialog */}
      <Dialog
        isOpen={isActivateConfirmOpen}
        onClose={() => setIsActivateConfirmOpen(false)}
        width="w-[95%] sm:w-[450px]"
        padding="px-6 py-8 sm:px-10 sm:py-10"
        className="z-[60]"
        id="activate-blitz-confirmation-dialog"
      >
        <div className="text-center">
          <h3 className="text-heading font-heading mb-2">Activate Blitz Mode</h3>
          <p className="text-text-secondary text-body mb-8">Are you sure you want to activate blitz mode?</p>

          <div className="flex justify-center gap-4">
            <button
              onClick={() => setIsActivateConfirmOpen(false)}
              className="btn-secondary px-8"
              id="cancel-activate-blitz"
            >
              Cancel
            </button>
            <button
              onClick={handleActivateBlitzMode}
              className="btn-primary px-8"
              id="confirm-activate-blitz"
              data-testid="confirm-activate-button"
            >
              Activate
            </button>
          </div>
        </div>
      </Dialog>

      {/* Deactivate Warning Dialog */}
      <Dialog
        isOpen={isDeactivateWarningOpen}
        onClose={() => setIsDeactivateWarningOpen(false)}
        width="w-[95%] sm:w-[500px]"
        padding="px-6 py-8 sm:px-10 sm:py-10"
        className="z-[60]"
        id="deactivate-warning-dialog"
      >
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-yellow-50 flex items-center justify-center">
              <Clock className="w-8 h-8 text-yellow-500" />
            </div>
          </div>
          <h3 className="text-heading font-heading mb-2">Cancel Blitz Schedule</h3>
          <p className="text-text-secondary text-body mb-2">
            Blitz mode is currently scheduled from:
          </p>
          {blitzStatus?.schedule && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-sm">
              <div className="mb-1">
                <span className="font-medium">Start:</span> {`${formatDate(blitzStatus.schedule.startDate)}, ${blitzStatus.schedule.startTime} ET`}
              </div>
              <div>
                <span className="font-medium">End:</span> {`${formatDate(blitzStatus.schedule.endDate)}, ${blitzStatus.schedule.endTime} ET`}
              </div>
            </div>
          )}
          <p className="text-text-secondary text-body mb-8">
            {blitzStatus?.isActive
              ? 'This will stop blitz mode and cancel the scheduled session. Do you want to continue?'
              : 'Are you sure you want to cancel the scheduled blitz mode session?'}
          </p>

          <div className="flex justify-center gap-4">
            <button
              onClick={() => setIsDeactivateWarningOpen(false)}
              className="btn-secondary px-8"
              id="cancel-deactivate-warning"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                setIsDeactivateWarningOpen(false);
                if (blitzStatus?.isActive) {
                  setIsDeactivateConfirmOpen(true);
                } else {
                  handleCancelSchedule();
                }
              }}
              className="btn-primary px-8"
              id="proceed-with-deactivation"
              data-testid="proceed-with-deactivation"
            >
              Confirm
            </button>
          </div>
        </div>
      </Dialog>

      {/* Deactivate Confirmation Dialog */}
      <Dialog
        isOpen={isDeactivateConfirmOpen}
        onClose={() => setIsDeactivateConfirmOpen(false)}
        width="w-[95%] sm:w-[450px]"
        padding="px-6 py-8 sm:px-10 sm:py-10"
        className="z-[60]"
        id="deactivate-blitz-confirmation-dialog"
      >
        <div className="text-center">
          <h3 className="text-heading font-heading mb-2">Deactivate Blitz Mode</h3>
          <p className="text-text-secondary text-body mb-8">
            {blitzStatus?.schedule?.isScheduled
              ? 'Are you sure? This will stop blitz mode and cancel the scheduled session.'
              : 'Are you sure you want to deactivate blitz mode?'}
          </p>

          <div className="flex justify-center gap-4">
            <button
              onClick={() => setIsDeactivateConfirmOpen(false)}
              className="btn-secondary px-8"
              id="cancel-deactivate-blitz"
            >
              Cancel
            </button>
            <button
              onClick={handleDeactivateBlitzMode}
              className="btn-primary px-8"
              id="confirm-deactivate-blitz"
              data-testid="confirm-deactivate-button"
            >
              Deactivate
            </button>
          </div>
        </div>
      </Dialog>

      {/* Success Dialog */}
      <Dialog
        isOpen={isSuccessDialogOpen}
        onClose={() => { setIsSuccessDialogOpen(false); setSuccessScheduleInfo(null); }}
        width="w-[95%] sm:w-[450px]"
        padding="px-6 py-8 sm:px-10 sm:py-10"
        className="z-[60]"
        id="blitz-success-dialog"
      >
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center">
              <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
          <h2 className="text-2xl font-medium mb-4">
            {successScheduleInfo ? 'Blitz Mode is Scheduled' : 'Success!'}
          </h2>

          {successScheduleInfo ? (
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-sm">
              <p className="text-gray-700">
                <span className="font-medium">From:</span> {`${formatDate(successScheduleInfo.startDate)}, ${successScheduleInfo.startTime}:00 ET`}
              </p>
              <p className="text-gray-700">
                <span className="font-medium">To:</span> {`${formatDate(successScheduleInfo.endDate)}, ${successScheduleInfo.endTime}:00 ET`}
              </p>
              {blitzStatus?.isActive && (
                <p className="text-green-600 font-medium pt-2">Blitz mode is currently active</p>
              )}
            </div>
          ) : (
            <p className="text-gray-600 mb-6">
              {successMessage}
            </p>
          )}
          <button
            onClick={() => { setIsSuccessDialogOpen(false); setSuccessScheduleInfo(null); }}
            className="btn-primary px-8"
          >
            Done
          </button>
        </div>
      </Dialog>
    </header>
  );
}
